import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import { useSelector } from 'react-redux';
import styles from './BundleGiftAOVItem.module.scss';
import chevronDownBigSvg from '../../../../assets/images/svg/chevron-down-big.svg';
import messages from './messages';

const BundleGiftAOVItem = ({ item, selectedSku = null, showBundleItems }) => {
  const { sanitize } = DOMPurify;
  const { formatMessage, locale } = useIntl();
  const [isDetailsOpened, setIsDetailsOpened] = useState(false);
  const [detailsSectionsState, setDetailsSectionsState] = useState({});
  const { productsSpecsAndMaterials } = useSelector((state) => state.pdp);

  useState(() => {
   setIsDetailsOpened(!showBundleItems);
  }, [])

  const itemInfo = useMemo(
    () =>
      productsSpecsAndMaterials.data?.find(
        (pdp) => pdp.attributes.field_product_key === item.gift.productKey,
      ),
    [item.gift.productKey, productsSpecsAndMaterials],
  );

  const accordionItems = useMemo(
    () =>
      itemInfo?.relationships?.field_specs_and_materials?.data
        ?.map((node) =>
          productsSpecsAndMaterials.included?.find(
            (iNode) => iNode.id === node.id,
          ),
        )
        ?.filter((node) => !!node),
    [itemInfo, productsSpecsAndMaterials],
  );

  const getAccordionItemContent = (accordionItem) => {
    const paragraphs = accordionItem.relationships.field_p_items?.data
      ?.map((node) =>
        productsSpecsAndMaterials.included?.find(
          (iNode) => iNode.id === node.id,
        ),
      )
      ?.filter((node) => !!node);

    return paragraphs.map((paragraph) => (
      <div key={paragraph.id} className={styles.paragraph}>
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: sanitize(paragraph.attributes?.field_heading?.processed),
          }}
          className={styles['paragraph-title']}
        />
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: sanitize(
              paragraph.attributes?.field_description?.processed,
            ),
          }}
          className={styles['paragraph-content']}
        />
      </div>
    ));
  };

  const itemImage = (
    item.gift.variants?.find((variant) => variant.sku === selectedSku) ??
    item.gift.variants?.[0]
  )?.image;

  return (
    <div className={styles.container}>
      {showBundleItems && (
        <div className={styles['top-section']}>
          <div className={styles['item-basic-info']}>
            <img
              src={itemImage}
              alt={formatMessage(messages.productThumbnail)}
            />
            <div className={styles['product-title']}>
              {item.gift.productName[locale]}
              {item.gift.quantity > 1 ? ` (${item.gift.quantity})` : null}
            </div>
          </div>
          <button
            className={classNames(styles['item-toggle-button'], {
              [styles.opened]: isDetailsOpened,
            })}
            type="button"
            onClick={() => setIsDetailsOpened((prev) => !prev)}
          >
            <img src={chevronDownBigSvg} alt={messages.toggleProductCard} />
          </button>
        </div>
      )}

      {isDetailsOpened && (
        <div className={styles['bottom-section']}>
          {showBundleItems && (
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitize(item.gift.description[locale]),
              }}
            />
          )}

          <div>
            {accordionItems?.map((accordionItem) => (
              <div
                className={styles['product-details-section']}
                key={accordionItem.id}
              >
                <div className={styles['product-details-toggle']}>
                  <div
                    className={styles['product-details-section-title']}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: sanitize(
                        accordionItem.attributes?.field_label?.processed,
                      ),
                    }}
                  />
                  <button
                    className={classNames(
                      styles['product-details-section-toggle'],
                      {
                        [styles.opened]: detailsSectionsState[accordionItem.id],
                      },
                    )}
                    type="button"
                    onClick={() =>
                      setDetailsSectionsState((prev) => ({
                        ...prev,
                        [accordionItem.id]: !prev[accordionItem.id],
                      }))
                    }
                  >
                    <img
                      src={chevronDownBigSvg}
                      alt={formatMessage(messages.detailsToggle)}
                    />
                  </button>
                </div>
                {detailsSectionsState[accordionItem.id] && (
                  <div className={styles['product-details-content']}>
                    {getAccordionItemContent(accordionItem)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

BundleGiftAOVItem.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    gift: PropTypes.object.isRequired,
  }).isRequired,
  selectedSku: PropTypes.string,
  showBundleItems: PropTypes.bool.isRequired
};

export default BundleGiftAOVItem;
